import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import {
  completeGuardianOAuth,
  fetchGuardianAuthMethods,
  fetchGuardianOAuthUrl,
  guardianLogin,
  guardianRegister,
  requestGuardianPasswordReset,
  resetGuardianPassword,
  sendGuardianEmailVerificationCode,
  validateGuardianResetToken,
  verifyGuardianEmailCode,
} from '../api/guardianApi';
import GuardianAuthShell from './GuardianAuthShell';
import TermsLinks from './TermsLinks';
import { CAPTCHA_DISABLED, executeRecaptcha, preloadRecaptcha } from '../utils/recaptcha';
import { safeSessionStorage, safeStorage } from '../utils/safeStorage';

const OAUTH_REDIRECT_URI =
  process.env.REACT_APP_GUARDIAN_OAUTH_REDIRECT_URI
  || `${window.location.origin}${window.location.pathname}`;

function persistSession(result, onAuthenticated) {
  // A failed write only means the session will not survive a reload
  // (2026-09-25 gap closure).
  safeStorage.set('guardianAuthToken', result.data.authToken);
  safeStorage.set('guardianUser', JSON.stringify(result.data.user));
  onAuthenticated(result.data.authToken, result.data.user, result.data.entitlements);
}

export default function GuardianAuth({
  onAuthenticated,
  initialMode = 'register',
  resetToken: resetTokenProp = null,
  onModeChange,
  onCancel,
}) {
  const [mode, setMode] = useState(initialMode);
  const [registerStep, setRegisterStep] = useState('email');
  const [resetToken, setResetToken] = useState(resetTokenProp);
  const [resetEmail, setResetEmail] = useState(null);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    verificationCode: '',
    password: '',
    confirmPassword: '',
  });
  const [signupToken, setSignupToken] = useState(null);
  // Terms + privacy acceptance at sign-up (audit P-4); shown only when the
  // Backend publishes a version (/auth/methods terms.required).
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(false);
  const [authMethods, setAuthMethods] = useState({
    email: true,
    phone: false,
    google: false,
    microsoft: false,
  });

  useEffect(() => {
    setMode(initialMode);
    if (initialMode === 'register') {
      setRegisterStep('email');
      setSignupToken(null);
    }
    if (initialMode === 'reset' && resetTokenProp) {
      setResetToken(resetTokenProp);
    }
  }, [initialMode, resetTokenProp]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchGuardianAuthMethods();
        if (!cancelled && res.success && res.data) {
          setAuthMethods(res.data);
        }
      } catch {
        // Soft-fail: email auth still works if methods endpoint is unreachable.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!CAPTCHA_DISABLED) {
      preloadRecaptcha().catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (mode !== 'reset' || !resetToken) {
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setValidatingToken(true);
      setError(null);
      setInfo(null);
      try {
        const result = await validateGuardianResetToken(resetToken);
        if (cancelled) return;
        if (!result.success) {
          setError(result.message || 'This reset link is invalid or has expired');
          setResetEmail(null);
          return;
        }
        setResetEmail(result.data?.email || null);
        setInfo('Choose a new password for your account.');
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to validate reset link');
        }
      } finally {
        if (!cancelled) {
          setValidatingToken(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mode, resetToken]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const resetRegisterFlow = () => {
    setRegisterStep('email');
    setSignupToken(null);
    setForm((prev) => ({
      ...prev,
      verificationCode: '',
      password: '',
      confirmPassword: '',
    }));
  };

  const goToMode = (nextMode) => {
    setMode(nextMode);
    onModeChange?.(nextMode);
    setError(null);
    setInfo(null);
    if (nextMode === 'register') {
      resetRegisterFlow();
    }
    if (nextMode !== 'reset') {
      setForm((prev) => ({ ...prev, password: '', confirmPassword: '' }));
    }
  };

  const handleSendVerificationCode = async (e) => {
    e.preventDefault();
    setError(null);
    setInfo(null);

    const email = form.email.trim().toLowerCase();
    if (!email) {
      setError('Email is required');
      return;
    }

    setLoading(true);
    try {
      const captcha = await executeRecaptcha('guardian_email_verify');
      const result = await sendGuardianEmailVerificationCode(email, captcha);
      if (!result.success) {
        setError(result.message || 'Failed to send verification email');
        return;
      }
      setInfo(`We sent a verification code to ${email}. Check your inbox.`);
      setRegisterStep('verify');
    } catch (err) {
      setError(err.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmailCode = async (e) => {
    e.preventDefault();
    setError(null);
    setInfo(null);

    const email = form.email.trim().toLowerCase();
    const code = form.verificationCode.trim();
    if (!code) {
      setError('Verification code is required');
      return;
    }

    setLoading(true);
    try {
      const result = await verifyGuardianEmailCode(email, code);
      if (!result.success || !result.data?.signupToken) {
        setError(result.message || 'Invalid or expired verification code');
        return;
      }
      setSignupToken(result.data.signupToken);
      setInfo('Email verified. Finish setting up your account.');
      setRegisterStep('details');
    } catch (err) {
      setError(err.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      const captcha = await executeRecaptcha('guardian_email_verify');
      const result = await sendGuardianEmailVerificationCode(form.email.trim(), captcha);
      if (!result.success) {
        setError(result.message || 'Failed to resend verification email');
        return;
      }
      setInfo('A new verification code has been sent.');
    } catch (err) {
      setError(err.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError(null);
    setInfo(null);

    const email = form.email.trim().toLowerCase();
    if (!email) {
      setError('Email is required');
      return;
    }

    setLoading(true);
    try {
      const captcha = await executeRecaptcha('guardian_forgot_password');
      const result = await requestGuardianPasswordReset(email, captcha);
      if (!result.success) {
        setError(result.message || 'Failed to send reset email');
        return;
      }
      setInfo(
        result.message
        || 'If an account exists for this email, a password reset link has been sent.'
      );
    } catch (err) {
      setError(err.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError(null);
    setInfo(null);

    if (!resetToken) {
      setError('Missing reset token. Request a new password reset link.');
      return;
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const captcha = await executeRecaptcha('guardian_reset_password');
      const result = await resetGuardianPassword(resetToken, form.password, captcha);
      if (!result.success) {
        setError(result.message || 'Failed to update password');
        return;
      }
      setResetToken(null);
      setResetEmail(null);
      setForm((prev) => ({ ...prev, password: '', confirmPassword: '' }));
      goToMode('login');
      setInfo('Password updated. Sign in with your new password.');
    } catch (err) {
      setError(err.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setInfo(null);

    const email = form.email.trim().toLowerCase();
    if (!email) {
      setError('Email is required');
      return;
    }

    if (mode === 'register') {
      const firstName = form.firstName.trim();
      const lastName = form.lastName.trim();
      if (!firstName || !lastName) {
        setError('First and last name are required');
        return;
      }
      if (!signupToken) {
        setError('Verify your email before creating an account');
        setRegisterStep('email');
        return;
      }
      if (form.password.length < 8) {
        setError('Password must be at least 8 characters');
        return;
      }
      if (form.password !== form.confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      if (authMethods.terms?.required && !termsAccepted) {
        setError('Accept the Terms of Service and Privacy Policy to create an account');
        return;
      }
    }

    setLoading(true);
    try {
      const captchaAction = mode === 'login' ? 'guardian_login' : 'guardian_register';
      const captcha = await executeRecaptcha(captchaAction);

      if (mode === 'register') {
        const registerResult = await guardianRegister({
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email,
          password: form.password,
          signupToken,
          captcha,
          acceptTermsVersion: authMethods.terms?.required ? authMethods.terms.version : undefined,
        });
        if (!registerResult.success) {
          setError(registerResult.message || 'Registration failed');
          if ((registerResult.message || '').toLowerCase().includes('verification')) {
            resetRegisterFlow();
          }
          return;
        }

        const loginCaptcha = await executeRecaptcha('guardian_login');
        const loginResult = await guardianLogin(email, form.password, loginCaptcha);
        if (!loginResult.success || !loginResult.data?.authToken) {
          setInfo('Account created. Sign in with your email and password.');
          goToMode('login');
          setError(loginResult.message || 'Account created, but sign-in failed. Try signing in.');
          return;
        }
        persistSession(loginResult, onAuthenticated);
        return;
      }

      const result = await guardianLogin(email, form.password, captcha);
      if (!result.success || !result.data?.authToken) {
        setError(result.message || 'Invalid email or password');
        return;
      }
      persistSession(result, onAuthenticated);
    } catch (err) {
      setError(err.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const shellTitle = {
    register: 'Sign up',
    login: 'Sign in',
    forgot: 'Forgot password',
    reset: 'Reset password',
  }[mode] || 'Sign in';

  const shellSubtitle = {
    register: registerStep === 'email'
      ? 'Enter your email and we will send a verification code'
      : registerStep === 'verify'
        ? 'Enter the code we sent to your email'
        : 'Create your Guardian account to report incidents and access field intelligence',
    login: 'Access Guardian field intelligence and incident reporting',
    forgot: 'Enter your email and we will send a reset link if an account exists',
    reset: resetEmail
      ? `Set a new password for ${resetEmail}`
      : 'Set a new password for your Guardian account',
  }[mode];

  const handleOAuth = async (provider) => {
    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      const result = await fetchGuardianOAuthUrl(provider, OAUTH_REDIRECT_URI);
      if (!result.success || !result.data?.authorize_url || !result.data?.state) {
        setError(result.message || `${provider} sign-in is not available`);
        return;
      }
      // Without stored state the return leg cannot be verified: fail closed
      // before leaving the page (2026-09-25 gap closure).
      const stored = safeSessionStorage.set('guardian_oauth_provider', provider)
        && safeSessionStorage.set('guardian_oauth_state', result.data.state);
      if (!stored) {
        setError('Sign-in with this provider needs browser storage. Enable site data and try again.');
        return;
      }
      window.location.assign(result.data.authorize_url);
    } catch (err) {
      setError(err.message || 'OAuth sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  const registerForm = (
    <>
      {registerStep === 'email' && (
        <Box component="form" onSubmit={handleSendVerificationCode} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Email"
            type="email"
            value={form.email}
            onChange={handleChange('email')}
            required
            autoComplete="email"
            autoFocus
          />
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Please wait…' : 'Send verification code'}
          </Button>
        </Box>
      )}

      {registerStep === 'verify' && (
        <Box component="form" onSubmit={handleVerifyEmailCode} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Code sent to {form.email.trim()}
          </Typography>
          <TextField
            label="Verification code"
            value={form.verificationCode}
            onChange={handleChange('verificationCode')}
            required
            autoFocus
            inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code' }}
          />
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Please wait…' : 'Verify email'}
          </Button>
          <Button variant="text" onClick={handleResendCode} disabled={loading} sx={{ color: 'primary.main' }}>
            Resend code
          </Button>
          <Button variant="text" onClick={resetRegisterFlow} disabled={loading} sx={{ color: 'text.secondary' }}>
            Use a different email
          </Button>
        </Box>
      )}

      {registerStep === 'details' && (
        <Box component="form" onSubmit={handleEmailSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {form.email.trim()} verified
          </Typography>
          <TextField label="First name" value={form.firstName} onChange={handleChange('firstName')} required autoFocus />
          <TextField label="Last name" value={form.lastName} onChange={handleChange('lastName')} required />
          <TextField
            label="Password"
            type="password"
            value={form.password}
            onChange={handleChange('password')}
            required
            autoComplete="new-password"
            inputProps={{ minLength: 8 }}
          />
          <TextField
            label="Confirm password"
            type="password"
            value={form.confirmPassword}
            onChange={handleChange('confirmPassword')}
            required
            autoComplete="new-password"
            inputProps={{ minLength: 8 }}
          />
          {authMethods.terms?.required && (
            <FormControlLabel
              control={(
                <Checkbox
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                />
              )}
              label={<span>I accept the <TermsLinks terms={authMethods.terms} /></span>}
            />
          )}
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Please wait…' : 'Create account'}
          </Button>
        </Box>
      )}
    </>
  );

  const loginForm = (
    <Box component="form" onSubmit={handleEmailSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField label="Email" type="email" value={form.email} onChange={handleChange('email')} required autoComplete="email" />
      <TextField
        label="Password"
        type="password"
        value={form.password}
        onChange={handleChange('password')}
        required
        autoComplete="current-password"
      />
      <Button type="submit" variant="contained" disabled={loading}>
        {loading ? 'Please wait…' : 'Sign in with email'}
      </Button>
    </Box>
  );

  const authLinkSx = {
    p: 0,
    minWidth: 0,
    justifyContent: 'flex-start',
    textTransform: 'none',
    fontWeight: 400,
    fontSize: '0.875rem',
    lineHeight: 1.43,
    color: 'text.secondary',
  };

  const forgotForm = (
    <Box component="form" onSubmit={handleForgotPassword} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label="Email"
        type="email"
        value={form.email}
        onChange={handleChange('email')}
        required
        autoComplete="email"
        autoFocus
      />
      <Button type="submit" variant="contained" disabled={loading}>
        {loading ? 'Please wait…' : 'Send reset link'}
      </Button>
      <Button variant="text" onClick={() => goToMode('login')} disabled={loading} sx={{ color: 'text.secondary' }}>
        Back to sign in
      </Button>
    </Box>
  );

  const resetForm = (
    <Box component="form" onSubmit={handleResetPassword} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {validatingToken ? (
        <Typography variant="body2" color="text.secondary">
          Validating reset link…
        </Typography>
      ) : (
        <>
          <TextField
            label="New password"
            type="password"
            value={form.password}
            onChange={handleChange('password')}
            required
            autoComplete="new-password"
            autoFocus
            disabled={!resetToken || Boolean(error && !resetEmail)}
            inputProps={{ minLength: 8 }}
          />
          <TextField
            label="Confirm new password"
            type="password"
            value={form.confirmPassword}
            onChange={handleChange('confirmPassword')}
            required
            autoComplete="new-password"
            disabled={!resetToken || Boolean(error && !resetEmail)}
            inputProps={{ minLength: 8 }}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={loading || validatingToken || !resetToken || Boolean(error && !resetEmail)}
          >
            {loading ? 'Please wait…' : 'Update password'}
          </Button>
        </>
      )}
      <Button
        variant="text"
        onClick={() => {
          setResetToken(null);
          setResetEmail(null);
          goToMode('forgot');
        }}
        disabled={loading}
        sx={{ color: 'text.secondary' }}
      >
        Request a new reset link
      </Button>
    </Box>
  );

  const showSocial = mode === 'login' || mode === 'register';

  let primaryForm = loginForm;
  if (mode === 'register') primaryForm = registerForm;
  if (mode === 'forgot') primaryForm = forgotForm;
  if (mode === 'reset') primaryForm = resetForm;

  const authForm = (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {info && <Alert severity="info" sx={{ mb: 2 }}>{info}</Alert>}

      {primaryForm}

      {(mode === 'login' || mode === 'register') && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, mt: 2 }}>
          {mode === 'login' && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                component="span"
                variant="body2"
                sx={{ color: 'text.secondary', lineHeight: 1.43 }}
              >
                Forgot Password?
              </Typography>
              <Button
                variant="text"
                onClick={() => goToMode('forgot')}
                disabled={loading}
                sx={{
                  ...authLinkSx,
                  ml: 0.75,
                  color: 'primary.main',
                  fontWeight: 600,
                  lineHeight: 1.43,
                }}
              >
                Click here to reset it
              </Button>
            </Box>
          )}
          <Button
            variant="text"
            onClick={() => goToMode(mode === 'login' ? 'register' : 'login')}
            sx={{ ...authLinkSx, color: 'primary.main', fontWeight: 600 }}
          >
            {mode === 'login' ? 'Need an account? Sign up' : 'Already have an account? Sign in'}
          </Button>
        </Box>
      )}

      {showSocial && (
        <>
          <Divider sx={{ my: 3, color: 'text.secondary', borderColor: 'divider' }}>
            or continue with
          </Divider>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Button
              variant="outlined"
              disabled={loading || !authMethods.google}
              onClick={() => handleOAuth('google')}
            >
              Continue with Google
            </Button>
            <Button
              variant="outlined"
              disabled={loading || !authMethods.microsoft}
              onClick={() => handleOAuth('microsoft')}
            >
              Continue with Microsoft
            </Button>
            {!authMethods.google && !authMethods.microsoft && (
              <Typography variant="caption" color="text.secondary">
                Social sign-in is not configured on this environment. Use email to sign in.
              </Typography>
            )}
          </Box>
        </>
      )}
    </>
  );

  return (
    <GuardianAuthShell
      title={shellTitle}
      subtitle={shellSubtitle}
      onCancel={onCancel}
    >
      {authForm}
    </GuardianAuthShell>
  );
}

/** Parse a password-reset token from the current URL (SPA-safe query or path). */
export function getGuardianResetTokenFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const path = window.location.pathname || '';
  if (/reset-password/i.test(path)) {
    return params.get('token') || params.get('resetToken');
  }
  return params.get('resetToken');
}

export function clearGuardianResetTokenFromUrl() {
  const url = new URL(window.location.href);
  const onResetPath = /reset-password/i.test(url.pathname);
  if (!url.searchParams.has('resetToken') && !url.searchParams.has('token') && !onResetPath) {
    return;
  }
  url.searchParams.delete('resetToken');
  url.searchParams.delete('token');
  if (onResetPath) {
    url.pathname = '/';
  }
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
}

export async function handleGuardianOAuthReturn(onAuthenticated) {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const state = params.get('state');
  const provider = safeSessionStorage.get('guardian_oauth_provider');
  const storedState = safeSessionStorage.get('guardian_oauth_state');
  if (!code || !provider || !state || !storedState || state !== storedState) {
    return false;
  }

  const result = await completeGuardianOAuth(provider, code, OAUTH_REDIRECT_URI, state);
  safeSessionStorage.remove('guardian_oauth_provider');
  safeSessionStorage.remove('guardian_oauth_state');
  window.history.replaceState({}, '', window.location.pathname);

  if (!result.success || !result.data?.authToken) {
    throw new Error(result.message || 'OAuth sign-in failed');
  }
  persistSession(result, onAuthenticated);
  return true;
}
